import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Upload, Save } from "lucide-react";
import { businessInfo } from "@/data/mockData";
import { toast } from "sonner";

export default function BusinessInfo() {
  const [form, setForm] = useState(businessInfo);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSave = (e) => {
    e.preventDefault();
    toast.success("Business info saved (demo)");
  };

  return (
    <div className="space-y-8" data-testid="business-info-page">
      <PageHeader
        title="Business Information"
        subtitle="Update your business details, branding, and contact information."
      />

      <form onSubmit={onSave} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="business-info-form">
        {/* Logo & brand */}
        <Card className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 lg:col-span-1">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            Brand
          </div>
          <div className="mt-5 flex flex-col items-center text-center">
            <div className="h-24 w-24 rounded-2xl bg-primary flex items-center justify-center shadow-sm">
              <Building2 className="h-10 w-10 text-white" strokeWidth={1.5} />
            </div>
            <div className="mt-4 font-display text-lg font-semibold text-slate-900 dark:text-slate-100">
              {form.name}
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="upload-logo-button"
              onClick={() => toast.message("Demo: Upload logo")}
              className="mt-4 rounded-lg"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Logo
            </Button>
          </div>
        </Card>

        {/* Fields */}
        <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 lg:col-span-2 space-y-6">
          <div>
            <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] mb-4">
              Details
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="bn" className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">Business Name</Label>
                <Input id="bn" data-testid="bi-name" value={form.name} onChange={onChange("name")} className="h-11 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">Phone</Label>
                <Input id="phone" data-testid="bi-phone" value={form.phone} onChange={onChange("phone")} className="h-11 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">Email</Label>
                <Input id="email" data-testid="bi-email" value={form.email} onChange={onChange("email")} className="h-11 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hours" className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">Hours</Label>
                <Input id="hours" data-testid="bi-hours" value={form.hours} onChange={onChange("hours")} className="h-11 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="area" className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">Service Area</Label>
                <Input id="area" data-testid="bi-area" value={form.serviceArea} onChange={onChange("serviceArea")} className="h-11 rounded-lg" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="about" className="text-xs font-medium uppercase tracking-wider text-slate-700 dark:text-slate-300">About Your Business</Label>
                <Textarea id="about" data-testid="bi-about" value={form.about} onChange={onChange("about")} rows={4} className="rounded-lg resize-none" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="outline"
              data-testid="bi-cancel"
              onClick={() => setForm(businessInfo)}
              className="rounded-lg"
            >
              Reset
            </Button>
            <Button type="submit" data-testid="bi-save" className="bg-primary hover:bg-primary/90 rounded-lg btn-premium">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
